import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "../../../components/ui-components/Modal";
import UserSearchInput from "../../../components/form-components/UserSearchInput";
import Input from "../../../components/form-components/Input";
import { Dropdown } from "../../../components/form-components/Dropdown";
import { ROLES, ROLE_LABELS } from "../policyBuilderState";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

const ROLE_OPTIONS = ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }));

function mapFromStep(step) {
  if (!step) return null;
  return {
    approverType: step.approver_type || "role",
    role: step.role || "finance_owner",
    userValue: step.approver_type === "user" && step.user_id
      ? { user_id: String(step.user_id), display_name: step.label || step.user_id }
      : null,
    approvalMode: step.approval_mode || "any_of",
    label: step.label || "",
  };
}

const DEFAULT_STATE = {
  approverType: "role",
  role: "finance_owner",
  userValue: null,
  approvalMode: "any_of",
  label: "",
};

const AddStepModal = ({ show, onClose, onSubmit, mode = "add", initialStep = null }) => {
  const [approverType, setApproverType] = useState("role");
  const [role, setRole] = useState("finance_owner");
  const [userValue, setUserValue] = useState(null);
  const [approvalMode, setApprovalMode] = useState("any_of");
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (show) {
      const next = mode === "edit" && initialStep ? mapFromStep(initialStep) : DEFAULT_STATE;
      setApproverType(next?.approverType ?? "role");
      setRole(next?.role ?? "finance_owner");
      setUserValue(next?.userValue ?? null);
      setApprovalMode(next?.approvalMode ?? "any_of");
      setLabel(next?.label ?? "");
    }
  }, [show, mode, initialStep]);

  const reset = () => {
    setApproverType("role");
    setRole("finance_owner");
    setUserValue(null);
    setApprovalMode("any_of");
    setLabel("");
  };

  const handleConfirm = () => {
    const stepDraft = {
      approver_type: approverType,
      role: approverType === "role" ? role : null,
      user_id: approverType === "user" ? (userValue?.user_id || null) : null,
      approval_mode: approvalMode,
      label: label || (approverType === "role" ? ROLE_LABELS[role] : (userValue?.display_name || "User")),
      allow_duplicate_override: mode === "edit" && initialStep ? !!initialStep.allow_duplicate_override : false,
    };
    onSubmit(stepDraft, mode === "edit" ? initialStep : null);
    reset();
    onClose();
  };

  const isEdit = mode === "edit";

  return (
    <Modal
      show={show}
      close={() => { reset(); onClose(); }}
      title={isEdit ? "Edit approver step" : "Add approver step"}
      confirmButton={isEdit ? "Save changes" : "Add step"}
      cancelButton="Cancel"
      confirmAction={handleConfirm}
      icon={icon({ name: "plus", style: "solid" })}
    >
      <div className="space-y-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-text-2 mb-1">Approver type</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-sm text-text-2 cursor-pointer">
              <input
                type="radio"
                name="approverType"
                value="role"
                checked={approverType === "role"}
                onChange={() => setApproverType("role")}
              />
              Role
            </label>
            <label className="flex items-center gap-1.5 text-sm text-text-2 cursor-pointer">
              <input
                type="radio"
                name="approverType"
                value="user"
                checked={approverType === "user"}
                onChange={() => setApproverType("user")}
              />
              Specific user
            </label>
          </div>
        </div>

        {approverType === "role" && (
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">Role</label>
            <Dropdown
              options={ROLE_OPTIONS}
              selectedValue={ROLE_OPTIONS.find((o) => o.value === role) || ROLE_OPTIONS[0]}
              setSelectedValue={(opt) => setRole(opt.value)}
              dropdownWidth={300}
            />
          </div>
        )}

        {approverType === "user" && (
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">User</label>
            <UserSearchInput
              value={userValue}
              onChange={setUserValue}
              placeholder="Search user by name or email..."
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text-2 mb-1">Approval mode</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-sm text-text-2 cursor-pointer">
              <input
                type="radio"
                name="approvalMode"
                value="any_of"
                checked={approvalMode === "any_of"}
                onChange={() => setApprovalMode("any_of")}
              />
              Any of
            </label>
            <label className="flex items-center gap-1.5 text-sm text-text-2 cursor-pointer">
              <input
                type="radio"
                name="approvalMode"
                value="all_of"
                checked={approvalMode === "all_of"}
                onChange={() => setApprovalMode("all_of")}
              />
              All of
            </label>
          </div>
        </div>

        <Input
          name="label"
          title="Label (optional)"
          type="text"
          placeholder={approverType === "role" ? ROLE_LABELS[role] : "User"}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          noMarginBottom
          width="300px"
        />
      </div>
    </Modal>
  );
};

AddStepModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialStep: PropTypes.object,
};

export default AddStepModal;
